/**
 * Shared entrance-animation presets.
 *
 * These exist for one load-bearing reason beyond deduplication: react-snap
 * crawls the built app with headless Chrome and writes the rendered DOM back
 * into static HTML. Framer Motion starts every element at its `initial` state,
 * so an unguarded snapshot captures the whole site at `opacity: 0` — all the
 * content present, none of it visible. That is worse than no prerender at all,
 * because search engines devalue hidden text and a visitor without JavaScript
 * gets a blank page.
 *
 * react-snap identifies itself via the user agent, so during a snapshot the
 * `initial` state is dropped entirely (`initial={false}`) and elements render
 * in their settled state. Real browsers are unaffected and still animate.
 */
export const IS_PRERENDER =
  typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

/**
 * True when this document arrived as prerendered HTML rather than an empty
 * shell. Evaluated at module load, which happens before hydration replaces the
 * markup, so #root still holds the snapshot at this point.
 *
 * It matters because prerendered content is already painted. If framer then
 * applies its `initial` state on hydration, the visitor sees the page appear,
 * blank out, and fade back in — a flicker that undoes the reason for
 * prerendering in the first place.
 */
export const HYDRATED_FROM_PRERENDER =
  typeof document !== "undefined" &&
  (document.getElementById("root")?.childElementCount ?? 0) > 0;

/** The site's single easing curve. */
export const EASE = [0.22, 1, 0.36, 1];

const from = (y) => (IS_PRERENDER ? false : { opacity: 0, y });

/**
 * Plays on mount. Used for above-the-fold content, which is on screen from the
 * first paint — so on a prerendered load its `initial` state is skipped
 * entirely and the content simply stays where it already is. Scroll reveals
 * (below) keep their entrance, since those elements are off screen when the
 * page hydrates and nobody sees them settle.
 */
export function entrance({ y = 24, duration = 0.8, delay = 0 } = {}) {
  return {
    initial: HYDRATED_FROM_PRERENDER ? false : from(y),
    animate: { opacity: 1, y: 0 },
    transition: { duration, delay, ease: EASE },
  };
}

/**
 * Plays when the element scrolls into view, once.
 */
export function reveal({
  y = 24,
  duration = 0.55,
  delay = 0,
  margin = "-40px",
} = {}) {
  return {
    initial: from(y),
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin },
    transition: { duration, delay, ease: EASE },
  };
}
